import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
    getOrganizationsAdmin,
    createOrganizationAdmin,
    updateOrganizationAdmin,
    deleteOrganizationAdmin,
    getOrganizationStatsAdmin,
    getTeamsAdmin
} from '../services/WebCloudFunctionsService';
import {
    Plus,
    Edit,
    Trash2,
    Building,
    Users,
    Settings,
    RefreshCw,
    CheckCircle,
    XCircle,
    Globe,
    Shield,
    Target,
    AlertCircle,
    Download,
    Upload
} from 'lucide-react';

interface Organization {
    id: string;
    name: string;
    domain: string;
    description?: string;
    logoURL?: string;
    isActive: boolean;
    memberCount: number;
    teamCount: number;
    createdDate: string;
    settings: {
        allowPublicJoin: boolean;
        requireApproval: boolean;
        customChallenges: boolean;
        leaderboardVisible: boolean;
        teamCreationOpen: boolean;
    };
}

interface Team {
    id: string;
    name: string;
    organizationId: string;
    memberCount: number;
    createdDate: string;
    isActive: boolean;
}

interface OrganizationFormData {
    name: string;
    domain: string;
    description: string;
    isActive: boolean;
    settings: {
        allowPublicJoin: boolean;
        requireApproval: boolean;
        customChallenges: boolean;
        leaderboardVisible: boolean;
        teamCreationOpen: boolean;
    };
}

const Organizations: React.FC = () => {
    const { theme } = useTheme();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [teams, setTeams] = useState<Team[]>([]);
    const [filteredOrganizations, setFilteredOrganizations] = useState<Organization[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [formData, setFormData] = useState<OrganizationFormData>({
        name: '',
        domain: '',
        description: '',
        isActive: true,
        settings: {
            allowPublicJoin: true,
            requireApproval: false,
            customChallenges: true,
            leaderboardVisible: true,
            teamCreationOpen: true,
        },
    });

    // Sample data for demo
    const sampleOrganizations: Organization[] = [
        {
            id: 'uh',
            name: 'University Hospitals',
            domain: 'uhhospitals.org',
            description: 'Healthcare organization focused on wellness and fitness',
            isActive: true,
            memberCount: 1250,
            teamCount: 8,
            createdDate: '2024-01-01T00:00:00Z',
            settings: {
                allowPublicJoin: false,
                requireApproval: true,
                customChallenges: true,
                leaderboardVisible: true,
                teamCreationOpen: false,
            },
        },
        {
            id: 'org1',
            name: 'Tech Corp',
            domain: 'techcorp.com',
            description: 'Technology company promoting employee wellness',
            isActive: true,
            memberCount: 450,
            teamCount: 12,
            createdDate: '2024-01-15T00:00:00Z',
            settings: {
                allowPublicJoin: true,
                requireApproval: false,
                customChallenges: true,
                leaderboardVisible: true,
                teamCreationOpen: true,
            },
        },
    ];

    const sampleTeams: Team[] = [
        {
            id: 'uh_cutler',
            name: 'UH Cutler',
            organizationId: 'uh',
            memberCount: 85,
            createdDate: '2024-01-02T00:00:00Z',
            isActive: true,
        },
        {
            id: 'uh_main',
            name: 'UH Main',
            organizationId: 'uh',
            memberCount: 120,
            createdDate: '2024-01-03T00:00:00Z',
            isActive: true,
        },
        {
            id: 'tech_engineering',
            name: 'Engineering',
            organizationId: 'org1',
            memberCount: 75,
            createdDate: '2024-01-16T00:00:00Z',
            isActive: true,
        },
    ];

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        filterOrganizations();
    }, [organizations, searchTerm, statusFilter]);

    const fetchOrganizations = async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('🔄 Fetching organizations with filters:', {
                isActive: statusFilter !== 'all' ? statusFilter === 'active' : undefined,
                searchTerm: searchTerm || undefined
            });

            const filters = {
                isActive: statusFilter !== 'all' ? statusFilter === 'active' : undefined,
                searchTerm: searchTerm || undefined
            };

            const [orgsData, teamsData] = await Promise.all([
                getOrganizationsAdmin(filters),
                getTeamsAdmin()
            ]);

            console.log('✅ Organizations and teams fetched successfully:', { orgsData, teamsData });

            // Ensure data is an array
            const organizationsArray: Organization[] = Array.isArray(orgsData) ? orgsData : [];
            const teamsArray: Team[] = Array.isArray(teamsData) ? teamsData : [];

            // If no organizations found, use sample data
            if (organizationsArray.length === 0) {
                console.log('🔄 No organizations found, using sample data');
                setOrganizations(sampleOrganizations);
                setTeams(sampleTeams);
            } else {
                setOrganizations(organizationsArray);
                setTeams(teamsArray);
            }
        } catch (err: any) {
            console.error('❌ Error fetching organizations:', err);
            console.log('🔄 Falling back to sample data');

            // Fallback to sample data when API fails
            setOrganizations(sampleOrganizations);
            setTeams(sampleTeams);
            setError(null); // Clear error since we have sample data
        } finally {
            setLoading(false);
        }
    };

    const filterOrganizations = () => {
        let filtered = organizations;

        if (searchTerm) {
            filtered = filtered.filter(org =>
                org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                org.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
                org.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (statusFilter !== 'all') {
            const isActive = statusFilter === 'active';
            filtered = filtered.filter(org => org.isActive === isActive);
        }

        setFilteredOrganizations(filtered);
    };

    const handleCreateOrganization = async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('🔄 Creating organization:', formData);

            const orgData = {
                name: formData.name,
                domain: formData.domain,
                description: formData.description,
                settings: formData.settings,
            };

            await createOrganizationAdmin(orgData);
            console.log('✅ Organization created successfully');

            // Refresh the organizations list
            await fetchOrganizations();
            setShowCreateModal(false);
            resetForm();
        } catch (err: any) {
            console.error('❌ Error creating organization:', err);
            setError(err.message || 'Failed to create organization');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateOrganization = async () => {
        if (!selectedOrganization) return;

        try {
            setLoading(true);
            setError(null);

            console.log('🔄 Updating organization:', { orgId: selectedOrganization.id, updates: formData });

            const updates = {
                name: formData.name,
                domain: formData.domain,
                description: formData.description,
                isActive: formData.isActive,
                settings: formData.settings,
            };

            await updateOrganizationAdmin(selectedOrganization.id, updates);
            console.log('✅ Organization updated successfully');

            // Refresh the organizations list
            await fetchOrganizations();
            setShowEditModal(false);
            resetForm();
        } catch (err: any) {
            console.error('❌ Error updating organization:', err);
            setError(err.message || 'Failed to update organization');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOrganization = async () => {
        if (!selectedOrganization) return;

        try {
            setLoading(true);
            setError(null);

            console.log('🔄 Deleting organization:', selectedOrganization.id);

            await deleteOrganizationAdmin(selectedOrganization.id);
            console.log('✅ Organization deleted successfully');

            // Refresh the organizations list
            await fetchOrganizations();
            setShowDeleteModal(false);
        } catch (err: any) {
            console.error('❌ Error deleting organization:', err);
            setError(err.message || 'Failed to delete organization');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            domain: '',
            description: '',
            isActive: true,
            settings: {
                allowPublicJoin: true,
                requireApproval: false,
                customChallenges: true,
                leaderboardVisible: true,
                teamCreationOpen: true,
            },
        });
    };

    const openEditModal = (org: Organization) => {
        setSelectedOrganization(org);
        setFormData({
            name: org.name,
            domain: org.domain,
            description: org.description || '',
            isActive: org.isActive,
            settings: org.settings,
        });
        setShowEditModal(true);
    };

    const openDeleteModal = (org: Organization) => {
        setSelectedOrganization(org);
        setShowDeleteModal(true);
    };

    const getStatusBadge = (isActive: boolean) => {
        return (
            <span style={{
                backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
                color: isActive ? '#166534' : '#dc2626',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
            }}>
                {isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                {isActive ? 'Active' : 'Inactive'}
            </span>
        );
    };

    const getOrganizationTeams = (orgId: string) => {
        return teams.filter(team => team.organizationId === orgId);
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                backgroundColor: theme.colors.background,
                color: theme.colors.text
            }}>
                <div style={{ textAlign: 'center' }}>
                    <RefreshCw className="animate-spin" size={32} />
                    <p style={{ marginTop: '16px', color: theme.colors.textSecondary }}>
                        Loading organizations...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            padding: '24px',
            backgroundColor: theme.colors.background,
            color: theme.colors.text,
            minHeight: '100vh',
            fontFamily: theme.fonts.primary
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
            }}>
                <div>
                    <h1 style={{
                        fontSize: theme.typography.h1.fontSize,
                        fontWeight: theme.typography.h1.fontWeight,
                        margin: '0 0 8px 0',
                        color: theme.colors.text
                    }}>
                        Organization Management
                    </h1>
                    <p style={{
                        fontSize: theme.typography.body.fontSize,
                        color: theme.colors.textSecondary,
                        margin: 0
                    }}>
                        Manage organizations, teams, and settings
                    </p>
                </div>

                <button
                    onClick={() => setShowCreateModal(true)}
                    style={{
                        backgroundColor: theme.colors.primary,
                        color: theme.colors.primaryText,
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'opacity 0.2s ease',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                    <Plus size={16} />
                    Add Organization
                </button>
            </div>

            {/* Filters */}
            <div style={{
                backgroundColor: theme.colors.surface,
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: `1px solid ${theme.colors.border}`,
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginBottom: '16px',
                }}>
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '600',
                            marginBottom: '8px',
                            color: theme.colors.text
                        }}>
                            Search Organizations
                        </label>
                        <input
                            type="text"
                            placeholder="Search by name, domain, or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: '8px',
                                fontSize: '14px',
                                backgroundColor: theme.colors.background,
                                color: theme.colors.text,
                            }}
                        />
                    </div>

                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '600',
                            marginBottom: '8px',
                            color: theme.colors.text
                        }}>
                            Status
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: '8px',
                                fontSize: '14px',
                                backgroundColor: theme.colors.background,
                                color: theme.colors.text,
                            }}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <span style={{
                        fontSize: '14px',
                        color: theme.colors.textSecondary
                    }}>
                        {filteredOrganizations.length} organizations found
                    </span>

                    <button
                        onClick={fetchOrganizations}
                        style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${theme.colors.border}`,
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: theme.colors.text,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                    >
                        <RefreshCw size={14} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Organizations Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                gap: '20px',
            }}>
                {filteredOrganizations.map((org) => (
                    <div key={org.id} style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: '12px',
                        border: `1px solid ${theme.colors.border}`,
                        padding: '20px',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '16px',
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '12px',
                                    backgroundColor: theme.colors.primary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: theme.colors.primaryText,
                                    fontSize: '20px',
                                }}>
                                    <Building size={24} />
                                </div>
                                <div>
                                    <h3 style={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        margin: '0 0 4px 0',
                                        color: theme.colors.text,
                                    }}>
                                        {org.name}
                                    </h3>
                                    <p style={{
                                        fontSize: '14px',
                                        color: theme.colors.textSecondary,
                                        margin: 0,
                                    }}>
                                        {org.domain}
                                    </p>
                                </div>
                            </div>
                            {getStatusBadge(org.isActive)}
                        </div>

                        {org.description && (
                            <p style={{
                                fontSize: '14px',
                                color: theme.colors.textSecondary,
                                marginBottom: '16px',
                                lineHeight: '1.5',
                            }}>
                                {org.description}
                            </p>
                        )}

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px',
                            marginBottom: '16px',
                        }}>
                            <div style={{
                                textAlign: 'center',
                                padding: '12px',
                                backgroundColor: theme.colors.background,
                                borderRadius: '8px',
                            }}>
                                <div style={{
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    color: theme.colors.primary,
                                    marginBottom: '4px',
                                }}>
                                    {org.memberCount}
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: theme.colors.textSecondary,
                                    textTransform: 'uppercase',
                                    fontWeight: '600',
                                }}>
                                    Members
                                </div>
                            </div>

                            <div style={{
                                textAlign: 'center',
                                padding: '12px',
                                backgroundColor: theme.colors.background,
                                borderRadius: '8px',
                            }}>
                                <div style={{
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    color: theme.colors.success,
                                    marginBottom: '4px',
                                }}>
                                    {org.teamCount}
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: theme.colors.textSecondary,
                                    textTransform: 'uppercase',
                                    fontWeight: '600',
                                }}>
                                    Teams
                                </div>
                            </div>
                        </div>

                        {/* Teams List */}
                        {getOrganizationTeams(org.id).length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                                <h4 style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    margin: '0 0 8px 0',
                                    color: theme.colors.text,
                                }}>
                                    Teams
                                </h4>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '6px',
                                }}>
                                    {getOrganizationTeams(org.id).map(team => (
                                        <span key={team.id} style={{
                                            backgroundColor: theme.colors.background,
                                            color: theme.colors.text,
                                            padding: '4px 8px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                        }}>
                                            {team.name} ({team.memberCount})
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Settings Summary */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px',
                            marginBottom: '16px',
                        }}>
                            {org.settings.allowPublicJoin && (
                                <span style={{
                                    backgroundColor: '#dcfce7',
                                    color: '#166534',
                                    padding: '2px 6px',
                                    borderRadius: '8px',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                }}>
                                    Public Join
                                </span>
                            )}
                            {org.settings.requireApproval && (
                                <span style={{
                                    backgroundColor: '#fef3c7',
                                    color: '#92400e',
                                    padding: '2px 6px',
                                    borderRadius: '8px',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                }}>
                                    Approval Required
                                </span>
                            )}
                            {org.settings.customChallenges && (
                                <span style={{
                                    backgroundColor: '#dbeafe',
                                    color: '#1e40af',
                                    padding: '2px 6px',
                                    borderRadius: '8px',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                }}>
                                    Custom Challenges
                                </span>
                            )}
                        </div>

                        {/* Actions */}
                        <div style={{
                            display: 'flex',
                            gap: '8px',
                        }}>
                            <button
                                onClick={() => openEditModal(org)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${theme.colors.border}`,
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    color: theme.colors.text,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                            >
                                <Edit size={12} />
                                Edit
                            </button>
                            <button
                                onClick={() => openDeleteModal(org)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${theme.colors.error}`,
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    color: theme.colors.error,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                            >
                                <Trash2 size={12} />
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {filteredOrganizations.length === 0 && !loading && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    textAlign: 'center',
                    backgroundColor: theme.colors.surface,
                    borderRadius: '12px',
                    border: `1px solid ${theme.colors.border}`,
                }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: '16px',
                    }}>
                        🏢
                    </div>
                    <h3 style={{
                        fontSize: '24px',
                        fontWeight: '600',
                        color: theme.colors.text,
                        margin: '0 0 8px 0',
                    }}>
                        No Organizations Found
                    </h3>
                    <p style={{
                        fontSize: '16px',
                        color: theme.colors.textSecondary,
                        margin: '0 0 24px 0',
                        maxWidth: '400px',
                        lineHeight: '1.5',
                    }}>
                        Get started by creating your first organization. Organizations help you manage teams and challenges.
                    </p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        style={{
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.primaryText,
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <Plus size={16} />
                        Create First Organization
                    </button>
                </div>
            )}

            {/* Create Organization Modal */}
            {showCreateModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                }}>
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: '12px',
                        padding: '24px',
                        width: '90%',
                        maxWidth: '500px',
                        border: `1px solid ${theme.colors.border}`,
                    }}>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            margin: '0 0 20px 0',
                            color: theme.colors.text,
                        }}>
                            Create Organization
                        </h2>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '600',
                                marginBottom: '8px',
                                color: theme.colors.text,
                            }}>
                                Organization Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: `1px solid ${theme.colors.border}`,
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: theme.colors.background,
                                    color: theme.colors.text,
                                }}
                                placeholder="Enter organization name"
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '600',
                                marginBottom: '8px',
                                color: theme.colors.text,
                            }}>
                                Domain *
                            </label>
                            <input
                                type="text"
                                value={formData.domain}
                                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: `1px solid ${theme.colors.border}`,
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: theme.colors.background,
                                    color: theme.colors.text,
                                }}
                                placeholder="e.g., company.com"
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '600',
                                marginBottom: '8px',
                                color: theme.colors.text,
                            }}>
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: `1px solid ${theme.colors.border}`,
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: theme.colors.background,
                                    color: theme.colors.text,
                                    minHeight: '80px',
                                    resize: 'vertical',
                                }}
                                placeholder="Enter organization description"
                            />
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end',
                        }}>
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    resetForm();
                                }}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${theme.colors.border}`,
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: theme.colors.text,
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateOrganization}
                                disabled={!formData.name || !formData.domain || loading}
                                style={{
                                    backgroundColor: theme.colors.primary,
                                    color: theme.colors.primaryText,
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1,
                                }}
                            >
                                {loading ? 'Creating...' : 'Create Organization'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Organization Modal */}
            {showEditModal && selectedOrganization && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                }}>
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: '12px',
                        padding: '24px',
                        width: '90%',
                        maxWidth: '500px',
                        border: `1px solid ${theme.colors.border}`,
                    }}>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            margin: '0 0 20px 0',
                            color: theme.colors.text,
                        }}>
                            Edit Organization
                        </h2>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '600',
                                marginBottom: '8px',
                                color: theme.colors.text,
                            }}>
                                Organization Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: `1px solid ${theme.colors.border}`,
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: theme.colors.background,
                                    color: theme.colors.text,
                                }}
                                placeholder="Enter organization name"
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '600',
                                marginBottom: '8px',
                                color: theme.colors.text,
                            }}>
                                Domain *
                            </label>
                            <input
                                type="text"
                                value={formData.domain}
                                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: `1px solid ${theme.colors.border}`,
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: theme.colors.background,
                                    color: theme.colors.text,
                                }}
                                placeholder="e.g., company.com"
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '600',
                                marginBottom: '8px',
                                color: theme.colors.text,
                            }}>
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: `1px solid ${theme.colors.border}`,
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: theme.colors.background,
                                    color: theme.colors.text,
                                    minHeight: '80px',
                                    resize: 'vertical',
                                }}
                                placeholder="Enter organization description"
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                color: theme.colors.text,
                                cursor: 'pointer',
                            }}>
                                <input
                                    type="checkbox"
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                    }}
                                />
                                Active Organization
                            </label>
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end',
                        }}>
                            <button
                                onClick={() => {
                                    setShowEditModal(false);
                                    resetForm();
                                }}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${theme.colors.border}`,
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: theme.colors.text,
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateOrganization}
                                disabled={!formData.name || !formData.domain || loading}
                                style={{
                                    backgroundColor: theme.colors.primary,
                                    color: theme.colors.primaryText,
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1,
                                }}
                            >
                                {loading ? 'Updating...' : 'Update Organization'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Organization Modal */}
            {showDeleteModal && selectedOrganization && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                }}>
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: '12px',
                        padding: '24px',
                        width: '90%',
                        maxWidth: '400px',
                        border: `1px solid ${theme.colors.border}`,
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '20px',
                        }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                backgroundColor: theme.colors.error,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                            }}>
                                <AlertCircle size={24} />
                            </div>
                            <div>
                                <h2 style={{
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    margin: '0 0 4px 0',
                                    color: theme.colors.text,
                                }}>
                                    Delete Organization
                                </h2>
                                <p style={{
                                    fontSize: '14px',
                                    color: theme.colors.textSecondary,
                                    margin: 0,
                                }}>
                                    This action cannot be undone
                                </p>
                            </div>
                        </div>

                        <p style={{
                            fontSize: '14px',
                            color: theme.colors.text,
                            marginBottom: '20px',
                            lineHeight: '1.5',
                        }}>
                            Are you sure you want to delete <strong>{selectedOrganization.name}</strong>?
                            This will also remove all associated teams and update user assignments.
                        </p>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end',
                        }}>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${theme.colors.border}`,
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: theme.colors.text,
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteOrganization}
                                disabled={loading}
                                style={{
                                    backgroundColor: theme.colors.error,
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1,
                                }}
                            >
                                {loading ? 'Deleting...' : 'Delete Organization'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    backgroundColor: theme.colors.error,
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    zIndex: 1001,
                }}>
                    {error}
                </div>
            )}
        </div>
    );
};

export default Organizations; 